import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  AsciiPanel,
  AsciiButton,
  AsciiTabs,
  AsciiStatusBadge,
} from "@/components/ui/ascii-theme";

type RecordingMode = "screen" | "webcam";

const ScreenRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [recordingMode, setRecordingMode] = useState<RecordingMode>("screen");
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [recordingFormat, setRecordingFormat] = useState<string>("video/webm");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const playbackVideoRef = useRef<HTMLVideoElement>(null);

  // Create blob URL for finished recording
  const recordingBlobUrl = useMemo(() => {
    if (recordedChunks.length === 0) return null;
    const blob = new Blob(recordedChunks, { type: recordingFormat });
    return URL.createObjectURL(blob);
  }, [recordedChunks, recordingFormat]);

  // Function to determine the best supported format
  const getBestSupportedFormat = useCallback(() => {
    const formats = [
      "video/mp4;codecs=avc1",
      "video/mp4",
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ];

    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        return format;
      }
    }

    return "video/webm"; // Fallback
  }, []);

  // Function to get file extension based on format
  const getFileExtension = useCallback((format: string) => {
    if (format.includes("mp4")) {
      return "mp4";
    }
    return "webm";
  }, []);

  const startRecording = useCallback(async () => {
    try {
      let stream: MediaStream;

      if (recordingMode === "screen") {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { mediaSource: "screen" } as any,
          audio: true,
        });
      } else if (recordingMode === "webcam") {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
      } else {
        // For now, just do screen recording - we'll implement picture-in-picture later
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: { mediaSource: "screen" } as any,
          audio: true,
        });
      }

      setMediaStream(stream);

      // Get the best supported format
      const supportedFormat = getBestSupportedFormat();
      setRecordingFormat(supportedFormat);

      // Set up MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: supportedFormat,
      });

      mediaRecorderRef.current = mediaRecorder;
      setRecordedChunks([]);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordedChunks((prev) => [...prev, event.data]);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        setMediaStream(null);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert(
        "Failed to start recording. Please ensure you grant the necessary permissions."
      );
    }
  }, [recordingMode, getBestSupportedFormat]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const downloadRecording = useCallback(() => {
    if (recordedChunks.length === 0) return;

    const blob = new Blob(recordedChunks, { type: recordingFormat });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const extension = getFileExtension(recordingFormat);
    a.download = `recording-${Date.now()}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [recordedChunks, recordingFormat, getFileExtension]);

  const uploadRecording = useCallback(async () => {
    if (recordedChunks.length === 0) return;

    const blob = new Blob(recordedChunks, { type: recordingFormat });
    const formData = new FormData();
    const extension = getFileExtension(recordingFormat);
    formData.append("video", blob, `recording-${Date.now()}.${extension}`);

    try {
      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        alert("Video uploaded successfully!");
        setRecordedChunks([]); // Clear the recording
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload video");
    }
  }, [recordedChunks, recordingFormat, getFileExtension]);

  // Cleanup blob URL when component unmounts or recording changes
  React.useEffect(() => {
    return () => {
      if (recordingBlobUrl) {
        URL.revokeObjectURL(recordingBlobUrl);
      }
    };
  }, [recordingBlobUrl]);

  // Handle media stream changes for live preview
  React.useEffect(() => {
    if (mediaStream && videoPreviewRef.current) {
      const video = videoPreviewRef.current;
      video.srcObject = mediaStream;
      video.muted = true;
      video.playsInline = true;

      const playVideo = async () => {
        try {
          await video.play();
        } catch (error) {
          console.error("Error playing video:", error);
        }
      };

      playVideo();
    } else if (!mediaStream && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  }, [mediaStream]);

  return (
    <div className="max-w-4xl mx-auto font-mono text-sm space-y-4 sm:space-y-6">
      {/* Status Bar */}
      <div className="border-b-2 border-black pb-3 mb-4 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-3">
          <AsciiStatusBadge status={isRecording ? "active" : "idle"}>
            {isRecording ? "RECORDING" : "IDLE"}
          </AsciiStatusBadge>
          <span className="opacity-60">v1.0.0</span>
        </div>
        <span className="opacity-60">
          FORMAT: {recordingFormat.includes("mp4") ? "MP4" : "WEBM"}
        </span>
      </div>

      {/* Control Panel */}
      <AsciiPanel title="═══ CONTROL PANEL ═══">
        <div className="space-y-4 sm:space-y-6">
          {/* Mode Selection */}
          <div>
            <div className="text-xs uppercase tracking-wide mb-3 opacity-60 font-bold">
              └─ MODE SELECT
            </div>
            <AsciiTabs
              tabs={[
                { id: "screen", label: "SCREEN" },
                { id: "webcam", label: "WEBCAM" },
              ]}
              activeTab={recordingMode}
              onChange={(id) => setRecordingMode(id as RecordingMode)}
              disabled={isRecording}
            />
          </div>

          {/* Recording Controls */}
          <div>
            <div className="text-xs uppercase tracking-wide mb-3 opacity-60 font-bold">
              └─ RECORDING CONTROLS
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              {!isRecording ? (
                <AsciiButton
                  onClick={startRecording}
                  variant="primary"
                  size="lg"
                >
                  ● START REC
                </AsciiButton>
              ) : (
                <>
                  <AsciiButton
                    onClick={stopRecording}
                    variant="danger"
                    size="lg"
                  >
                    ■ STOP REC
                  </AsciiButton>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 animate-pulse rounded-full"></div>
                    <AsciiStatusBadge status="active">
                      REC ACTIVE
                    </AsciiStatusBadge>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </AsciiPanel>

      {/* Live Preview Window */}
      {mediaStream && (
        <AsciiPanel title="═══ LIVE PREVIEW ═══">
          <div className="bg-black p-2 sm:p-4 border-2 border-black">
            <video
              ref={videoPreviewRef}
              autoPlay
              muted
              playsInline
              className="w-full border-2 border-gray-600"
            />
          </div>
        </AsciiPanel>
      )}

      {/* Recording Playback */}
      {recordedChunks.length > 0 && recordingBlobUrl && (
        <AsciiPanel title="═══ RECORDING PLAYBACK ═══">
          <div className="bg-black p-2 sm:p-4 border-2 border-black">
            <video
              ref={playbackVideoRef}
              controls
              className="w-full border-2 border-gray-600"
              src={recordingBlobUrl}
            />
          </div>
        </AsciiPanel>
      )}

      {/* Output Controls */}
      {recordedChunks.length > 0 && (
        <AsciiPanel title="═══ OUTPUT CONTROLS ═══">
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wide opacity-60 font-bold">
              └─ EXPORT OPTIONS
            </div>
            <div className="flex flex-wrap gap-3">
              <AsciiButton
                onClick={downloadRecording}
                variant="default"
                size="lg"
              >
                ⬇ DOWNLOAD
              </AsciiButton>
              <AsciiButton
                onClick={uploadRecording}
                variant="primary"
                size="lg"
              >
                ⬆ SAVE TO LIB
              </AsciiButton>
            </div>
          </div>
        </AsciiPanel>
      )}
    </div>
  );
};

export default ScreenRecorder;
